import React, { useState, useMemo } from 'react';
import { AdherenceStatistic } from '../services/adherenceService';

interface AdherenceHeatmapProps {
  data?: AdherenceStatistic[];
  loading?: boolean;
  error?: string | null;
}

interface WeekCell {
  weekStart: Date;
  weekEnd: Date;
  adherencePercentage: number | null;
  completedItems: number;
  totalItems: number;
}

interface MonthGroup {
  label: string;
  year: number;
  weeks: WeekCell[];
}

const getColor = (percentage: number | null): string => {
  if (percentage === null) return '#ebedf0';
  if (percentage === 0) return '#ebedf0';
  if (percentage <= 25) return '#c6f6d5';
  if (percentage <= 50) return '#68d391';
  if (percentage <= 75) return '#38a169';
  return '#276749';
};

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const CELL_SIZE = 22;
const CELL_GAP = 4;
const MONTH_GAP = 12;

const toLocalDateString = (d: Date): string => {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const buildMonthGroups = (data: AdherenceStatistic[]): MonthGroup[] => {
  const dataMap = new Map<string, AdherenceStatistic>();
  for (const item of data) {
    dataMap.set(item.weekStartDate, item);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const currentDay = today.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
  const currentWeekMonday = new Date(today);
  currentWeekMonday.setDate(today.getDate() + mondayOffset);

  const weeks: WeekCell[] = [];
  for (let i = 51; i >= 0; i--) {
    const weekStart = new Date(currentWeekMonday);
    weekStart.setDate(currentWeekMonday.getDate() - i * 7);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const key = toLocalDateString(weekStart);
    const stat = dataMap.get(key);

    weeks.push({
      weekStart: new Date(weekStart),
      weekEnd: new Date(weekEnd),
      adherencePercentage: stat ? stat.adherencePercentage : null,
      completedItems: stat ? stat.completedDiaryItems : 0,
      totalItems: stat ? stat.totalDiaryItems : 0,
    });
  }

  const groups: MonthGroup[] = [];
  for (const week of weeks) {
    const month = week.weekStart.getMonth();
    const year = week.weekStart.getFullYear();
    const last = groups[groups.length - 1];

    if (last && last.label === MONTH_LABELS[month] && last.year === year) {
      last.weeks.push(week);
    } else {
      groups.push({ label: MONTH_LABELS[month], year, weeks: [week] });
    }
  }

  return groups;
};

const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

const AdherenceHeatmap: React.FC<AdherenceHeatmapProps> = ({
  data = [],
  loading = false,
  error = null,
}) => {
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    cell: WeekCell;
  } | null>(null);

  const monthGroups = useMemo(() => buildMonthGroups(data), [data]);

  if (loading) {
    return (
      <div style={{ width: '100%', height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#666', fontSize: 14 }}>Cargando calendario de adherencia...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ width: '100%', height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#e53e3e', fontSize: 14, textAlign: 'center' }}>
          <div>Error al cargar datos</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div data-heatmap-root style={{ position: 'relative' }}>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, auto)',
          gap: `${MONTH_GAP}px ${MONTH_GAP}px`,
          justifyContent: 'start',
          marginLeft: 40,
          width: 'max-content',
        }}>
          {monthGroups.map((group, gi) => (
          <div key={`${group.label}-${group.year}-${gi}`}>
            {/* Month + year label */}
            <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 4, fontWeight: 600, userSelect: 'none' }}>
              {group.label} {group.year}
            </div>
            {/* Week squares */}
            <div style={{ display: 'flex', gap: CELL_GAP }}>
              {group.weeks.map((week, wi) => (
                <div
                  key={wi}
                  style={{
                    width: CELL_SIZE,
                    height: CELL_SIZE,
                    borderRadius: 3,
                    backgroundColor: getColor(week.adherencePercentage),
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const container = e.currentTarget.closest<HTMLElement>('[data-heatmap-root]');
                    if (!container) return;
                    const containerRect = container.getBoundingClientRect();
                    setTooltip({
                      x: rect.left - containerRect.left + CELL_SIZE / 2,
                      y: rect.bottom - containerRect.top + 8,
                      cell: week,
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                />
              ))}
            </div>
          </div>
        ))}
        </div>
      </div>

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 4,
          marginTop: 10,
          fontSize: 11,
          color: '#6b7280',
        }}
      >
        <span>Menos</span>
        {[null, 10, 35, 60, 90].map((pct, i) => (
          <div
            key={i}
            style={{
              width: CELL_SIZE,
              height: CELL_SIZE,
              borderRadius: 3,
              backgroundColor: getColor(pct),
            }}
          />
        ))}
        <span>Más</span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          data-heatmap-tooltip
          style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateX(-50%)',
            background: '#1f2937',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          <div style={{ fontWeight: 600 }}>
            {formatDate(tooltip.cell.weekStart)} – {formatDate(tooltip.cell.weekEnd)}
          </div>
          {tooltip.cell.adherencePercentage !== null ? (
            <>
              <div>Adherencia: {Math.round(tooltip.cell.adherencePercentage)}%</div>
              <div>
                {tooltip.cell.completedItems}/{tooltip.cell.totalItems} completados
              </div>
            </>
          ) : (
            <div>Sin datos</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdherenceHeatmap;
